
'use client';

import { useEffect, useRef, useMemo } from 'react';
import { useUser as useFirebaseUser, useFirestore, useMemoFirebase } from '@/firebase/provider';
import { createUserProfile, type UserProfile } from '@/firebase/data/users';
import { useDoc } from '../firestore/use-doc';
import { doc } from 'firebase/firestore';

export interface UserAuthHookResult {
  user: UserProfile | null;
  loading: boolean; // Alias for isUserLoading for backward compatibility
  isUserLoading: boolean;
  userError: Error | null;
}

/**
 * Hook for accessing the authenticated user's profile state.
 * Automatically creates a user profile in Firestore on first login.
 *
 * OPTIMIZED: Returns an optimistic profile immediately after auth completes,
 * then updates with Firestore data when available. This prevents white screens
 * while waiting for profile loading.
 *
 * @returns {UserAuthHookResult} Object with user profile, loading status, and any auth errors.
 */
export const useUser = (): UserAuthHookResult => {
  const { user: firebaseUser, isUserLoading: isAuthLoading, userError } = useFirebaseUser();
  const firestore = useFirestore();

  // Use uid instead of the entire user object for better stability
  const uid = firebaseUser?.uid;

  // Track if we've already initiated profile creation to prevent duplicate calls
  const profileCreationInitiatedRef = useRef<string | null>(null);

  const userProfileRef = useMemoFirebase(
    () => (uid && firestore ? doc(firestore, 'users', uid) : null),
    [uid, firestore]
  );

  const { data: userProfile, isLoading: isProfileLoading, error: profileError } = useDoc<UserProfile>(userProfileRef);

  // Create optimistic profile from Firebase Auth while waiting for Firestore
  // This allows the dashboard to render immediately without waiting for Firestore
  const optimisticProfile = useMemo((): UserProfile | null => {
    if (!firebaseUser) return null;
    if (userProfile) return userProfile; // Use real profile if available

    // Return optimistic profile with defaults from Firebase Auth
    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      name: firebaseUser.displayName || 'User',
      creditsBalance: 10, // Default free credits
      planTier: 'free',
      locale: 'en',
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
    };
  }, [firebaseUser, userProfile]);

  // Create profile only once when user first signs in
  useEffect(() => {
    // Only create profile if:
    // 1. We have a uid
    // 2. We have the full firebaseUser object
    // 3. Auth is not loading
    // 4. Profile is not loading
    // 5. Profile doesn't exist yet
    // 6. No profile error
    // 7. We haven't already initiated creation for this uid
    if (
      uid &&
      firebaseUser &&
      !isAuthLoading &&
      !isProfileLoading &&
      !userProfile &&
      !profileError &&
      profileCreationInitiatedRef.current !== uid
    ) {
      // Mark that we've initiated creation for this uid
      profileCreationInitiatedRef.current = uid;

      const { email, displayName } = firebaseUser;

      const newProfile: Omit<UserProfile, 'id' | 'createdAt' | 'lastLoginAt'> = {
        email: email || '',
        name: displayName || 'New User',
        creditsBalance: 10, // Free credits on signup
        planTier: 'free',
        locale: 'en',
      };

      // This is a fire-and-forget operation, but we catch errors to prevent unhandled promise rejections.
      createUserProfile(uid, newProfile).catch(err => {
        // Reset the ref on error so we can retry
        profileCreationInitiatedRef.current = null;
        // The error is already emitted globally by the non-blocking update function.
        // We just log it here for server-side visibility if needed, but avoid user-facing side-effects.
        console.error("Failed to initiate user profile creation:", err);
      });
    }
  }, [uid, firebaseUser, isAuthLoading, isProfileLoading, userProfile, profileError]);

  // CRITICAL OPTIMIZATION: Only block on auth loading, not profile loading
  // This allows the dashboard to render with optimistic data immediately
  return {
    user: optimisticProfile,
    loading: isAuthLoading, // Only block on auth, not profile
    isUserLoading: isAuthLoading,
    userError: userError || profileError,
  };
};
